// client/src/services/localPollsService.js
import { api } from './api';

// Path to local JSON file
const POLLS_FILE_PATH = '../components/polls-data.json';

// Initialize polls if file doesn't exist
const initPolls = async () => {
  try {
    const response = await fetch(POLLS_FILE_PATH);
    if (response.status === 404) {
      // Create empty polls file
      const initialData = { polls: [] };
      await savePollsData(initialData);
      return initialData;
    }
    return await response.json();
  } catch (error) {
    console.error('Error initializing polls:', error);
    return { polls: [] };
  }
};

// Save polls data to JSON file
const savePollsData = async (data) => {
  try {
    // In a real app, this would be a server-side operation
    // For demonstration, we'll just log what would be saved
    console.log('Saving polls data:', data);
    // In development, you might use localStorage
    localStorage.setItem('polls-data', JSON.stringify(data));
    return true;
  } catch (error) {
    // console.error('Error saving polls data:', error);
    return false;
  }
};

// Fetch polls data from JSON file or localStorage
const getPolls = async () => {
  try {
    const stored = localStorage.getItem('polls-data');
    if (stored) {
      return JSON.parse(stored);
    }
    return await initPolls();
  } catch (error) {
    // console.error('Error getting polls:', error);
    return { polls: [] };
  }
};

export const localPollsService = {
  getAllPolls: async () => {
    try {
      const data = await getPolls();
      return data.polls;
    } catch (error) {
      // console.error('Error fetching polls:', error);
      return [];
    }
  },

  getPoll: async (pollId) => {
    try {
      const data = await getPolls();
      return data.polls.find(poll => poll.pollId === pollId);
    } catch (error) {
      // console.error(`Error fetching poll ${pollId}:`, error);
      return null;
    }
  },

  createPoll: async (question, options, pollType = 'multiple-choice') => {
    try {
      const data = await getPolls();
      const pollId = Date.now().toString();
      
      const newPoll = {
        pollId,
        question,
        pollType,
        options: options || [], // Array of answer options for multiple-choice
        createdAt: Math.floor(Date.now() / 1000),
        status: 'pending',
        responses: {}
      };
      
      data.polls.unshift(newPoll); // Add to beginning of array
      await savePollsData(data);
      
      // Simulate generating responses from personas
      setTimeout(async () => {
        await generatePersonaResponses(pollId, question, options, pollType);
      }, 2000);
      
      return newPoll;
    } catch (error) {
      // console.error('Error creating poll:', error);
      throw error;
    }
  },

  deletePoll: async (pollId) => {
    try {
      const data = await getPolls();
      data.polls = data.polls.filter(poll => poll.pollId !== pollId);
      await savePollsData(data);
      return true;
    } catch (error) {
      // console.error('Error deleting poll:', error);
      throw error;
    }
  }
};

// Helper to generate fake persona responses
const generatePersonaResponses = async (pollId, question, options, pollType) => {
  try {
    // Get personas data from the existing personas in the app
    const personas = [
      { id: "1", name: "Emily" },
      { id: "2", name: "James" },
      { id: "3", name: "Sophie" },
      { id: "4", name: "Thomas" },
      { id: "5", name: "Sarah" }
    ];
    
    const responses = {};
    
    // For each persona, generate a response based on poll type
    personas.forEach(persona => {
      if (pollType === 'multiple-choice') {
        // For multiple choice, randomly select an option
        const selectedOption = options[Math.floor(Math.random() * options.length)];
        
        responses[persona.id] = {
          personaId: persona.id,
          personaName: persona.name,
          response: selectedOption
        };
      } else if (pollType === 'yes-no') {
        // For yes/no questions
        const choices = ['Yes', 'No'];
        const selectedOption = choices[Math.floor(Math.random() * choices.length)];
        
        responses[persona.id] = {
          personaId: persona.id,
          personaName: persona.name,
          response: selectedOption
        };
      } else if (pollType === 'rating') {
        // For rating questions (1-5)
        const rating = Math.floor(Math.random() * 5) + 1;
        
        responses[persona.id] = {
          personaId: persona.id,
          personaName: persona.name,
          response: rating.toString()
        };
      } else {
        // Fallback for text responses
        responses[persona.id] = {
          personaId: persona.id,
          personaName: persona.name,
          response: `${persona.name}'s perspective on ${question.toLowerCase()}.`
        };
      }
    });
    
    // Update the poll with responses
    const data = await getPolls();
    const pollIndex = data.polls.findIndex(poll => poll.pollId === pollId);
    
    if (pollIndex !== -1) {
      data.polls[pollIndex].responses = responses;
      data.polls[pollIndex].status = 'completed';
      await savePollsData(data);
    }
    
    return true;
  } catch (error) {
    // console.error('Error generating responses:', error);
    return false;
  }
};